/**
 * Backend to Frontend Transformers
 *
 * Transforms backend Sprint types to frontend TrainingSprintConfig types
 * Used when loading draft sprints to continue editing
 */

import { Sprint, SprintParticipant, SprintTeam, SprintActivity } from '../../../../../../pure-js/types/Sprint'
import { SprintPurpose } from '../../../../../../pure-js/enums/SprintPurpose'
import { ActivityType as BEActivityType } from '../../../../../../pure-js/enums/ActivityType'
import { sanitizeSprintActivityGameOptions } from '../../../../../../pure-js/libs/SprintActivityGameOptions'

// Import unassigned team ID prefix constant
const UNASSIGNED_TEAM_ID_PREFIX = 'unassigned_'
import { TrainingSprintConfig, Participant, Team, Activity, TrainingPurpose, ActivityType } from '../types'
import dayjs from 'dayjs'
import { PlayIcon, LightBulbIcon, ChartBarIcon } from '@heroicons/react/24/outline'
import { getActivityTemplateByType } from '../constants/activities'
import { getResolvedLoopCount } from '../utils/activityConfiguration'

const LEGACY_FIRST_GAME_ID_PREFIX = 'ACTIVITY_FIRST_GAME_SESSION-' // hardcoded — legacy Firestore data used this prefix

const isLegacyFirstGameActivity = (beActivity: SprintActivity): boolean =>
  beActivity.type === BEActivityType.LONG_GAME && Boolean(beActivity.id?.startsWith(LEGACY_FIRST_GAME_ID_PREFIX))

/**
 * Transform backend SprintPurpose to frontend TrainingPurpose
 *
 * @param {SprintPurpose} bePurpose - Backend sprint purpose.
 * @return {TrainingPurpose} Frontend training purpose.
 */
function transformPurposeToFE(bePurpose: SprintPurpose): TrainingPurpose {
  // Map backend SprintPurpose values to frontend TrainingPurpose values
  const purposeMap: Record<SprintPurpose, TrainingPurpose> = {
    [SprintPurpose.AWARENESS]: 'aware' as TrainingPurpose,
    [SprintPurpose.DEVELOPMENT]: 'train' as TrainingPurpose,
    [SprintPurpose.CUSTOM]: 'custom' as TrainingPurpose
  }

  return purposeMap[bePurpose] || ('aware' as TrainingPurpose)
}

/**
 * Transform backend SprintParticipant to frontend Participant
 *
 * @param {SprintParticipant} beParticipant - Backend sprint participant.
 * @return {Participant} Frontend participant.
 */
function transformParticipantToFE(beParticipant: SprintParticipant): Participant {
  return {
    id: beParticipant.userId || beParticipant.email || `temp-${Date.now()}-${Math.random()}`,
    name: beParticipant.name,
    email: beParticipant.email || ''
  }
}

/**
 * Transform backend SprintTeam to frontend Team
 * Maps participants to this team based on backend teamId
 *
 * @param {SprintTeam} beTeam - Backend sprint team.
 * @param {SprintParticipant[]} beParticipants - Backend sprint participants.
 * @param {number | undefined} sprintMaxParticipants - Shared sprint max participants per team.
 * @return {Team} Frontend team.
 */
function transformTeamToFE(
  beTeam: SprintTeam,
  beParticipants: SprintParticipant[],
  sprintMaxParticipants?: number
): Team {
  // Get participants for this team from backend
  const teamParticipants = beParticipants.filter((p) => p.teamId === beTeam.tempTeamId).map(transformParticipantToFE)

  // Use Sprint's global maxParticipantsPerTeam if available, otherwise calculate default
  const maxParticipants = sprintMaxParticipants || (teamParticipants.length > 0 ? teamParticipants.length + 2 : 5)

  return {
    id: beTeam.tempTeamId,
    name: beTeam.name,
    participants: teamParticipants,
    maxParticipants: maxParticipants, // Preserve max from Sprint or use calculated default
    language: beTeam.language || 'en' // Preserve language from backend
  }
}

/**
 * Transform backend SprintActivity to frontend Activity
 *
 * @param {SprintActivity} beActivity - Backend activity.
 * @return {Activity} Frontend activity.
 */
function transformActivityToFE(beActivity: SprintActivity): Activity {
  // Parse startDate and endDate to extract date and time
  const startDateTime = dayjs(beActivity.startDate)
  const endDateTime = dayjs(beActivity.endDate)

  // Map backend ActivityType to frontend ActivityType
  let feType: ActivityType
  let icon: React.ComponentType<{ className?: string }>

  switch (beActivity.type) {
    case BEActivityType.INTRODUCTION:
      feType = ActivityType.INTRODUCTION
      icon = ChartBarIcon
      break
    case BEActivityType.FIRST_GAME:
      feType = ActivityType.FIRST_GAME
      icon = PlayIcon
      break
    case BEActivityType.SHORT_GAME:
      feType = ActivityType.SHORT_GAME
      icon = PlayIcon
      break
    case BEActivityType.LONG_GAME:
      feType = isLegacyFirstGameActivity(beActivity) ? ActivityType.FIRST_GAME : ActivityType.LONG_GAME
      icon = PlayIcon
      break
    case BEActivityType.SHORT_REFLECTION:
      feType = ActivityType.SHORT_REFLECTION
      icon = LightBulbIcon
      break
    case BEActivityType.LONG_REFLECTION:
      feType = ActivityType.LONG_REFLECTION
      icon = LightBulbIcon
      break
    default:
      feType = ActivityType.INTRODUCTION
      icon = ChartBarIcon
  }

  const template = getActivityTemplateByType(feType)
  const sanitizedRuntimeConfig = sanitizeSprintActivityGameOptions(beActivity)
  const sanitizedTemplateRuntimeConfig = sanitizeSprintActivityGameOptions(template)

  return {
    id: beActivity.id,
    title: beActivity.title,
    description: beActivity.description || '',
    type: feType,
    icon,
    scheduledDate: startDateTime.format('YYYY-MM-DD'),
    scheduledTime: startDateTime.format('HH:mm'),
    endTime: endDateTime.format('HH:mm'),
    duration: endDateTime.diff(startDateTime, 'minute'),
    numberOfLoops: getResolvedLoopCount(
      feType,
      sanitizedRuntimeConfig.numberOfLoops ?? sanitizedTemplateRuntimeConfig.numberOfLoops
    ),
    customSlotLength: sanitizedRuntimeConfig.customSlotLength ?? sanitizedTemplateRuntimeConfig.customSlotLength,
    minTimeLeftToAllowRestart:
      sanitizedRuntimeConfig.minTimeLeftToAllowRestart ?? sanitizedTemplateRuntimeConfig.minTimeLeftToAllowRestart,
    finalReflectionTimeOut:
      sanitizedRuntimeConfig.finalReflectionTimeOut ?? sanitizedTemplateRuntimeConfig.finalReflectionTimeOut,
    limit: undefined, // Not stored in backend
    isFixed: false, // Assume not fixed when loading
    // not shown in the wizard, carried through so saving the sprint keeps the activity's version
    ...(beActivity.dataCustomizationVariantId && { dataCustomizationVariantId: beActivity.dataCustomizationVariantId })
  }
}

/**
 * Main transformation function: Backend Sprint to Frontend TrainingSprintConfig
 *
 * This transforms a backend Sprint object into the frontend TrainingSprintConfig format
 * for use in the training sprint creation flow.
 *
 * @param {Sprint} sprint - Backend sprint.
 * @return {TrainingSprintConfig} Frontend sprint config.
 */
export function transformBEToTrainingSprintConfig(sprint: Sprint): TrainingSprintConfig {
  // Helper to check if team ID is unassigned placeholder
  const isUnassignedTeamId = (teamId?: string) => teamId?.startsWith(UNASSIGNED_TEAM_ID_PREFIX) || false

  // Filter out teams that are unassigned placeholders
  const realTeams = sprint.teams.filter((team) => !isUnassignedTeamId(team.tempTeamId))

  // Transform only real teams (not unassigned placeholders), passing Sprint's maxParticipantsPerTeam
  const teams = realTeams.map((team) => transformTeamToFE(team, sprint.participants, sprint.maxParticipantsPerTeam))

  // Get ALL participants - including those in the unassigned placeholder team
  const participants = sprint.participants.map(transformParticipantToFE)

  // Transform activities
  const activities = sprint.activities.map(transformActivityToFE)

  return {
    purpose: transformPurposeToFE(sprint.purpose),
    sprintName: sprint.title,
    activities,
    participants,
    teams,
    startDate: sprint.startDate || '',
    endDate: sprint.endDate || '',
    emailContent: sprint.emailContent
  }
}

/**
 * Check if a sprint is a draft (can be edited)
 *
 * @param {Sprint} sprint - Sprint to inspect.
 * @return {boolean} True when sprint is inactive.
 */
export function isSprintDraft(sprint: Sprint): boolean {
  return sprint.status === 'inactive'
}

/**
 * Check if a sprint is launched/active
 *
 * @param {Sprint} sprint - Sprint to inspect.
 * @return {boolean} True when sprint is active.
 */
export function isSprintActive(sprint: Sprint): boolean {
  return sprint.status === 'active'
}
